import type { SlowIdeasSelector, SlowIdeasSelectorEventBus, SlowMovingIdea } from '.';
import { signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import { inject, injectable } from 'src/features/ioc';
import { UniversePlanDTO } from 'src/features/universe/dtos/UniversePlanDTO';
import type { StoreAdapter } from 'src/models/StoreAdapter';
import type { ReactBindings } from 'src/types/bindings';
import type { SelectSlowIdeaEvent } from 'src/types/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';

@injectable()
export class SlowIdeasSelectorImpl implements SlowIdeasSelector {
    #disposableStack = new DisposableStack();
    #log: ReactBindings['Logger'];
    #selectedSlowIdeas: Signal<SlowMovingIdea[]> = signal([]);
    #slowIdea: Signal<IdeaDTO<IdeaPlanDTO> | null | undefined> = signal(undefined);
    #persistSelection: ((universeId: string | null) => void) | null = null;
    #onSlowIdeaSelected?: (payload: SelectSlowIdeaEvent['widget::selectSlowIdea']) => void;

    constructor(@inject('Logger') logger: ReactBindings['Logger']) {
        this.#log = logger.getSubLogger({ name: 'SlowIdeasSelector' });
    }

    eventBus: SlowIdeasSelectorEventBus = EventBusBuilder.create()
        .addSignal('widget::selectedSlowIdeas', () => this.#selectedSlowIdeas)
        .addSignal('widget::slowIdea', () => this.#slowIdea)
        .addEvent('widget::selectSlowIdea', (payload) => {
            const { ideas } = payload;

            if (!ideas[0]) {
                this.#selectedSlowIdeas.value = [];
                this.#slowIdea.value = null;
                this.#persistSelection?.(null);
            } else if ('universeId' in ideas[0]) {
                this.#selectedSlowIdeas.value = [{ type: 'universe', id: ideas[0].universeId }];
                this.#slowIdea.value = UniversePlanDTO.fromUniverseId(ideas[0].universeId).toTempIdea();
                this.#persistSelection?.(ideas[0].universeId);
            } else {
                const idea = ideas[0] as IdeaDTO;
                this.#selectedSlowIdeas.value = [{ type: 'idea', id: idea.idea.id as string }];
                this.#slowIdea.value = idea;
                this.#persistSelection?.(idea.idea.id as string);
            }

            this.#onSlowIdeaSelected?.(payload);
        })
        .build();

    setSlowIdea(slowIdea: IdeaDTO<IdeaPlanDTO> | null | undefined): void {
        this.#slowIdea.value = slowIdea;

        if (slowIdea) {
            const isUniverse = 'universeId' in slowIdea.plan;
            const id = isUniverse ? (slowIdea.plan as { universeId: string }).universeId : slowIdea.idea.id;
            if (id) {
                this.#selectedSlowIdeas.value = [{ type: isUniverse ? 'universe' : 'idea', id }];
            } else {
                this.#selectedSlowIdeas.value = [];
            }
        } else {
            this.#selectedSlowIdeas.value = [];
        }

        if (TRACE) {
            this.#log.trace('setSlowIdea', { slowIdea, selectedSlowIdeas: this.#selectedSlowIdeas.value });
        }
    }

    #syncFromUniverseId(universeId: string | null): void {
        const currentId = this.#selectedSlowIdeas.value[0]?.id ?? null;

        if (universeId !== currentId) {
            if (universeId === null) {
                this.#selectedSlowIdeas.value = [];
                this.#slowIdea.value = null;
            } else {
                this.#selectedSlowIdeas.value = [{ type: 'universe', id: universeId }];
                this.#slowIdea.value = UniversePlanDTO.fromUniverseId(universeId).toTempIdea();
            }
        }
    }

    init<T extends { universeId?: string | null }>(
        adapter: StoreAdapter<T>,
        options?: {
            onSlowIdeaSelected?: (payload: SelectSlowIdeaEvent['widget::selectSlowIdea']) => void;
            syncFromUniverseId?: boolean;
        },
    ): void {
        this.#onSlowIdeaSelected = options?.onSlowIdeaSelected;

        // When syncFromUniverseId is false, the widget manages the slow idea directly
        // via setSlowIdea() and universeId changes should not override that.
        // Skip both persisting and subscribing so that selectSlowIdea events
        // don't write to adapter.universeId, and adapter changes don't clear the slow idea.
        if (options?.syncFromUniverseId !== false) {
            this.#persistSelection = (universeId) => adapter.persist({ universeId } as Partial<T>);

            // Sync initial state
            this.#syncFromUniverseId(adapter.getState().universeId ?? null);

            // Subscribe to state changes
            const unsubscribe = adapter.subscribe((state) => {
                this.#syncFromUniverseId(state.universeId ?? null);
            });
            this.#disposableStack.defer(unsubscribe);
        }
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
